import React, { useMemo, useRef, useState, useCallback } from 'react';
import { sanitizeSvgMarkup } from '../utils/sanitize';
import {
  parseSvgForEditor,
  extractSvgDimensions,
  processSvgForRender,
  parseSvgStyleRules,
  svgHasCssDefinedFills,
  DEFAULT_STROKE,
  DEFAULT_GRADIENT,
} from '../components/Editor/svgUtils';

// ── Feature inventory helpers ────────────────────────────────────────────────

const FILTER_PRIMITIVES = [
  'feGaussianBlur','feOffset','feMerge','feMergeNode','feFlood','feComposite',
  'feBlend','feDropShadow','feColorMatrix','feComponentTransfer',
  'feFuncR','feFuncG','feFuncB','feFuncA',
  'feMorphology','feTurbulence','feDisplacementMap','feConvolveMatrix',
  'feSpecularLighting','feDiffuseLighting',
  'feDistantLight','fePointLight','feSpotLight',
  'feImage','feTile',
];

// Selector accepts either the bare local-name or the svg-namespaced variant
// that DOMParser produces in some browsers.
const countByLocalName = (root, name) =>
  Array.from(root.getElementsByTagName('*')).filter(
    (el) => el.nodeName.toLowerCase().replace(/^svg:/, '') === name.toLowerCase(),
  ).length;

const parseSvg = (text) => {
  const doc = new DOMParser().parseFromString(text || '', 'image/svg+xml');
  return { doc, err: doc.querySelector('parsererror') };
};

// Build the full feature inventory used in the compatibility report.
const inventorize = (svgText) => {
  if (!svgText) return null;
  const { doc, err } = parseSvg(svgText);
  if (err) return { parseError: true };

  const root = doc.documentElement;

  // Gradients
  const linearGradients     = countByLocalName(root, 'linearGradient');
  const radialGradients     = countByLocalName(root, 'radialGradient');
  const gradientStops       = countByLocalName(root, 'stop');
  const gradientTransforms  = Array.from(
    root.querySelectorAll('linearGradient[gradientTransform], radialGradient[gradientTransform]'),
  ).length;
  const gradientUnitsAttrs  = Array.from(
    root.querySelectorAll('linearGradient[gradientUnits], radialGradient[gradientUnits]'),
  ).length;
  const spreadMethodAttrs   = Array.from(
    root.querySelectorAll('linearGradient[spreadMethod], radialGradient[spreadMethod]'),
  ).length;
  const stopOpacities       = Array.from(root.querySelectorAll('stop[stop-opacity]')).length;

  // Clipping / masking
  const clipPaths   = countByLocalName(root, 'clipPath');
  const masks       = countByLocalName(root, 'mask');
  const clipPathRefs = Array.from(root.querySelectorAll('[clip-path]')).length;
  const maskRefs     = Array.from(root.querySelectorAll('[mask]')).length;

  // Filters
  const filters = countByLocalName(root, 'filter');
  const filterRefs = Array.from(root.querySelectorAll('[filter]')).length;
  const filterPrims = {};
  FILTER_PRIMITIVES.forEach((p) => {
    const n = countByLocalName(root, p);
    if (n > 0) filterPrims[p] = n;
  });

  // Groups, transforms, opacity
  const groups          = countByLocalName(root, 'g');
  const transformAttrs  = Array.from(root.querySelectorAll('[transform]')).length;
  const opacityAttrs    = Array.from(root.querySelectorAll('[opacity]')).length;
  const fillOpacityAttrs= Array.from(root.querySelectorAll('[fill-opacity]')).length;

  // viewBox / aspect
  const viewBox            = root.getAttribute('viewBox');
  const preserveAspectRatio= root.getAttribute('preserveAspectRatio');

  // Colors (attribute + style + <style>)
  const dims = extractSvgDimensions(svgText);
  const cssRules = parseSvgStyleRules(doc);
  const hasCssFills = svgHasCssDefinedFills(svgText);

  // Stop colors specifically (for the "metallic gold preservation" check)
  const stopColors = Array.from(root.querySelectorAll('stop')).map((s) =>
    s.getAttribute('stop-color') || (s.getAttribute('style') || '').match(/stop-color\s*:\s*([^;]+)/i)?.[1] || null,
  ).filter(Boolean);

  return {
    dims,
    viewBox,
    preserveAspectRatio,
    linearGradients,
    radialGradients,
    gradientStops,
    gradientTransforms,
    gradientUnitsAttrs,
    spreadMethodAttrs,
    stopOpacities,
    stopColors,
    clipPaths,
    masks,
    clipPathRefs,
    maskRefs,
    filters,
    filterRefs,
    filterPrims,
    groups,
    transformAttrs,
    opacityAttrs,
    fillOpacityAttrs,
    cssRules: cssRules.length,
    hasCssFills,
  };
};

// ── Comparison report ────────────────────────────────────────────────────────

const Row = ({ label, before, after, isCritical }) => {
  const preserved = JSON.stringify(before) === JSON.stringify(after);
  const cls = preserved
    ? 'text-emerald-700 bg-emerald-50 border-emerald-200'
    : isCritical
      ? 'text-rose-700 bg-rose-50 border-rose-200'
      : 'text-amber-700 bg-amber-50 border-amber-200';
  const icon = preserved ? '✓' : isCritical ? '✗' : '⚠';
  return (
    <tr className="border-t border-gray-200">
      <td className="px-3 py-2 text-sm text-gray-700">{label}</td>
      <td className="px-3 py-2 text-sm font-mono text-gray-900">{String(before ?? '—')}</td>
      <td className="px-3 py-2 text-sm font-mono text-gray-900">{String(after  ?? '—')}</td>
      <td className={`px-3 py-2 text-sm font-semibold border ${cls}`}>{icon} {preserved ? 'preserved' : 'changed'}</td>
    </tr>
  );
};

const FilterPrimsRow = ({ before, after }) => {
  const allKeys = Array.from(new Set([
    ...Object.keys(before || {}), ...Object.keys(after || {}),
  ])).sort();
  if (allKeys.length === 0) {
    return (
      <tr className="border-t border-gray-200">
        <td className="px-3 py-2 text-sm text-gray-700">Filter primitives</td>
        <td className="px-3 py-2 text-sm text-gray-400 italic" colSpan={2}>none in source</td>
        <td className="px-3 py-2 text-sm text-gray-400">n/a</td>
      </tr>
    );
  }
  return (
    <>
      {allKeys.map((k) => (
        <Row
          key={k}
          label={`  ${k}`}
          before={before?.[k] ?? 0}
          after={after?.[k] ?? 0}
          isCritical
        />
      ))}
    </>
  );
};

// ── Page component ───────────────────────────────────────────────────────────

const SAMPLE_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200">
  <defs>
    <linearGradient id="gold" x1="0" y1="0" x2="1" y2="1"
                    gradientUnits="objectBoundingBox" spreadMethod="pad"
                    gradientTransform="rotate(15)">
      <stop offset="0%"   stop-color="#fff7d6"/>
      <stop offset="45%"  stop-color="#d4af37"/>
      <stop offset="100%" stop-color="#8b6914" stop-opacity="0.95"/>
    </linearGradient>
    <radialGradient id="glow" cx="50%" cy="50%" r="50%">
      <stop offset="0%" stop-color="#fffbe8"/>
      <stop offset="100%" stop-color="#fff0a8" stop-opacity="0"/>
    </radialGradient>
    <filter id="dropShadow" x="-20%" y="-20%" width="140%" height="140%">
      <feGaussianBlur in="SourceAlpha" stdDeviation="3"/>
      <feOffset dx="2" dy="2" result="off"/>
      <feColorMatrix in="off" type="matrix"
        values="0 0 0 0 0   0 0 0 0 0   0 0 0 0 0   0 0 0 0.45 0"/>
      <feBlend in="SourceGraphic" in2="off" mode="normal"/>
    </filter>
    <clipPath id="rounded">
      <rect x="20" y="20" width="160" height="160" rx="20" ry="20"/>
    </clipPath>
    <style>.illusGold { fill: url(#gold); } .illusStroke { stroke: #8b6914; stroke-width: 2; }</style>
  </defs>
  <g clip-path="url(#rounded)" filter="url(#dropShadow)">
    <rect x="0" y="0" width="200" height="200" fill="url(#glow)"/>
    <circle class="illusGold illusStroke" cx="100" cy="100" r="60"/>
    <path d="M70 130 L100 70 L130 130 Z" style="fill:#c00;stroke:#600;stroke-width:2"/>
  </g>
</svg>`;

export default function SvgCompatibilityTest() {
  const [svgText, setSvgText] = useState(SAMPLE_SVG);
  const [colorOverride, setColorOverride] = useState(null);
  const fileRef = useRef(null);

  const onUpload = useCallback((file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => setSvgText(String(e.target.result || ''));
    reader.readAsText(file);
  }, []);

  // Build an editor element from the SVG just like UploadPanel does, so the
  // pipeline is identical to canvas rendering.
  const element = useMemo(() => {
    const parsed = parseSvgForEditor(svgText);
    const { w, h } = extractSvgDimensions(svgText);
    const map = { ...parsed.colorMap };
    if (colorOverride && parsed.colors[0]) {
      map[parsed.colors[0]] = colorOverride;
    }
    return {
      id: 'compat-test',
      type: 'svg',
      width: w, height: h,
      svgContent: parsed.svgContent,
      colors: parsed.colors,
      colorMap: map,
      fill: parsed.colors[0] || '#000000',
      gradient: { ...DEFAULT_GRADIENT },
      stroke:   { ...DEFAULT_STROKE },
      rotation: 0, opacity: 1,
    };
  }, [svgText, colorOverride]);

  const processed = useMemo(() => processSvgForRender(element), [element]);
  const originalInv  = useMemo(() => inventorize(svgText), [svgText]);
  const processedInv = useMemo(() => inventorize(processed), [processed]);

  if (!originalInv || originalInv.parseError) {
    return (
      <div className="p-8">
        <h1 className="text-2xl font-bold text-rose-600">SVG parse error</h1>
        <p className="text-gray-600 mt-2">The provided text isn't valid SVG.</p>
      </div>
    );
  }

  // Source counts for the headline scorecard
  const scorecard = [
    { k: 'Gradients',  ok: originalInv.linearGradients + originalInv.radialGradients === processedInv.linearGradients + processedInv.radialGradients },
    { k: 'Gradient stops', ok: originalInv.gradientStops === processedInv.gradientStops },
    { k: 'gradientTransform', ok: originalInv.gradientTransforms === processedInv.gradientTransforms },
    { k: 'gradientUnits', ok: originalInv.gradientUnitsAttrs === processedInv.gradientUnitsAttrs },
    { k: 'spreadMethod', ok: originalInv.spreadMethodAttrs === processedInv.spreadMethodAttrs },
    { k: 'stop-opacity', ok: originalInv.stopOpacities === processedInv.stopOpacities },
    { k: 'Filters',    ok: originalInv.filters === processedInv.filters },
    { k: 'Masks',      ok: originalInv.masks === processedInv.masks },
    { k: 'clipPaths',  ok: originalInv.clipPaths === processedInv.clipPaths },
    { k: 'clip-path refs', ok: originalInv.clipPathRefs === processedInv.clipPathRefs },
    { k: 'viewBox',    ok: originalInv.viewBox === processedInv.viewBox },
    { k: 'preserveAspectRatio', ok: (originalInv.preserveAspectRatio ?? null) === (processedInv.preserveAspectRatio ?? null) || processedInv.preserveAspectRatio != null },
    { k: 'CSS rules',  ok: originalInv.cssRules === processedInv.cssRules },
  ];

  const passCount = scorecard.filter((s) => s.ok).length;
  const totalCount = scorecard.length;
  const scorePct = Math.round((passCount / totalCount) * 100);

  // ── Render ──
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto p-6 space-y-6">
        <header className="space-y-1">
          <h1 className="text-2xl font-bold text-gray-900">SVG Fidelity / Compatibility Test</h1>
          <p className="text-sm text-gray-600">
            Compares the original SVG against the editor's processed output (same pipeline as the canvas).
            No rasterization, no flattening — both panels render live SVG DOMs.
          </p>
        </header>

        {/* Controls */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 flex flex-wrap gap-4 items-center">
          <button
            onClick={() => fileRef.current?.click()}
            className="px-4 py-2 rounded-md bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700"
          >
            Upload SVG
          </button>
          <input
            ref={fileRef}
            type="file"
            accept=".svg,image/svg+xml"
            className="hidden"
            onChange={(e) => onUpload(e.target.files?.[0])}
          />
          <button
            onClick={() => { setSvgText(SAMPLE_SVG); setColorOverride(null); }}
            className="px-3 py-2 rounded-md border border-gray-300 text-sm hover:bg-gray-50"
          >
            Reset to sample
          </button>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <span>Recolor first color →</span>
            <input
              type="color"
              value={colorOverride || '#ff0000'}
              onChange={(e) => setColorOverride(e.target.value)}
              className="w-10 h-8 rounded border border-gray-300"
            />
            <button
              onClick={() => setColorOverride(null)}
              className="text-xs text-gray-500 underline"
            >
              clear
            </button>
          </label>
        </div>

        {/* Scorecard */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-baseline justify-between">
            <h2 className="text-lg font-bold text-gray-900">Compatibility scorecard</h2>
            <span className={`text-2xl font-bold ${scorePct === 100 ? 'text-emerald-600' : scorePct >= 80 ? 'text-amber-600' : 'text-rose-600'}`}>
              {scorePct}%
            </span>
          </div>
          <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-2">
            {scorecard.map((s) => (
              <div
                key={s.k}
                className={`px-3 py-2 rounded border text-sm font-medium ${
                  s.ok
                    ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                    : 'bg-rose-50 border-rose-200 text-rose-800'
                }`}
              >
                {s.ok ? '✓' : '✗'} {s.k}
              </div>
            ))}
          </div>
        </div>

        {/* Side-by-side render */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <h3 className="text-sm font-bold text-gray-700 mb-2">Original SVG (browser native)</h3>
            <div
              className="aspect-square bg-[repeating-conic-gradient(#f3f4f6_0%_25%,#fff_0%_50%)] bg-[length:16px_16px] rounded overflow-hidden [&>svg]:w-full [&>svg]:h-full"
              dangerouslySetInnerHTML={{ __html: sanitizeSvgMarkup(svgText) }}
            />
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <h3 className="text-sm font-bold text-gray-700 mb-2">Processed SVG (editor pipeline)</h3>
            <div
              className="aspect-square bg-[repeating-conic-gradient(#f3f4f6_0%_25%,#fff_0%_50%)] bg-[length:16px_16px] rounded overflow-hidden [&>svg]:w-full [&>svg]:h-full"
              dangerouslySetInnerHTML={{ __html: processed }}
            />
          </div>
        </div>

        {/* Detailed inventory */}
        <div className="bg-white rounded-lg border border-gray-200">
          <h3 className="text-lg font-bold text-gray-900 px-4 pt-4">Feature inventory</h3>
          <table className="w-full mt-3">
            <thead>
              <tr className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                <th className="px-3 py-2">Feature</th>
                <th className="px-3 py-2">Original</th>
                <th className="px-3 py-2">Processed</th>
                <th className="px-3 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              <Row label="viewBox"             before={originalInv.viewBox} after={processedInv.viewBox} isCritical />
              <Row label="preserveAspectRatio" before={originalInv.preserveAspectRatio ?? '—'} after={processedInv.preserveAspectRatio ?? '—'} />
              <Row label="linearGradient count" before={originalInv.linearGradients} after={processedInv.linearGradients} isCritical />
              <Row label="radialGradient count" before={originalInv.radialGradients} after={processedInv.radialGradients} isCritical />
              <Row label="gradient stops"      before={originalInv.gradientStops} after={processedInv.gradientStops} isCritical />
              <Row label="gradientTransform attrs" before={originalInv.gradientTransforms} after={processedInv.gradientTransforms} isCritical />
              <Row label="gradientUnits attrs" before={originalInv.gradientUnitsAttrs} after={processedInv.gradientUnitsAttrs} isCritical />
              <Row label="spreadMethod attrs"  before={originalInv.spreadMethodAttrs} after={processedInv.spreadMethodAttrs} isCritical />
              <Row label="stop-opacity attrs"  before={originalInv.stopOpacities} after={processedInv.stopOpacities} isCritical />
              <Row label="stop colors (verbatim)" before={originalInv.stopColors.join(', ')} after={processedInv.stopColors.join(', ')} />
              <Row label="clipPath count"      before={originalInv.clipPaths} after={processedInv.clipPaths} isCritical />
              <Row label="clip-path references" before={originalInv.clipPathRefs} after={processedInv.clipPathRefs} isCritical />
              <Row label="mask count"          before={originalInv.masks} after={processedInv.masks} isCritical />
              <Row label="mask references"     before={originalInv.maskRefs} after={processedInv.maskRefs} isCritical />
              <Row label="filter count"        before={originalInv.filters} after={processedInv.filters} isCritical />
              <Row label="filter references"   before={originalInv.filterRefs} after={processedInv.filterRefs} isCritical />
              <tr className="border-t border-gray-200 bg-gray-50">
                <td className="px-3 py-2 text-xs uppercase tracking-wide text-gray-500" colSpan={4}>
                  Filter primitives
                </td>
              </tr>
              <FilterPrimsRow before={originalInv.filterPrims} after={processedInv.filterPrims} />
              <tr className="border-t border-gray-200 bg-gray-50">
                <td className="px-3 py-2 text-xs uppercase tracking-wide text-gray-500" colSpan={4}>
                  Structure
                </td>
              </tr>
              <Row label="<g> groups"          before={originalInv.groups} after={processedInv.groups} />
              <Row label="transform attrs"     before={originalInv.transformAttrs} after={processedInv.transformAttrs} isCritical />
              <Row label="opacity attrs"       before={originalInv.opacityAttrs} after={processedInv.opacityAttrs} />
              <Row label="fill-opacity attrs"  before={originalInv.fillOpacityAttrs} after={processedInv.fillOpacityAttrs} />
              <Row label="<style> rules"       before={originalInv.cssRules} after={processedInv.cssRules} isCritical />
              <Row label="has CSS-defined fills" before={originalInv.hasCssFills} after={processedInv.hasCssFills} />
            </tbody>
          </table>
        </div>

        {/* Side-by-side source */}
        <details className="bg-white rounded-lg border border-gray-200 p-4">
          <summary className="cursor-pointer text-sm font-bold text-gray-700">Show source SVG diff (text)</summary>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
            <div>
              <h4 className="text-xs uppercase text-gray-500 mb-1">Original</h4>
              <pre className="text-xs bg-gray-900 text-gray-100 p-3 rounded overflow-auto max-h-[400px] whitespace-pre-wrap">
                {svgText}
              </pre>
            </div>
            <div>
              <h4 className="text-xs uppercase text-gray-500 mb-1">Processed</h4>
              <pre className="text-xs bg-gray-900 text-gray-100 p-3 rounded overflow-auto max-h-[400px] whitespace-pre-wrap">
                {processed}
              </pre>
            </div>
          </div>
        </details>
      </div>
    </div>
  );
}
